import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type { Order, PermissionModel, PermissionSortingFields } from '../models';

interface IPermissionsContext {
  permissionsUpdate: boolean;
  togglePermissionsUpdate: () => void;
  permissions: PermissionModel[];
  setPermissions: (val: PermissionModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: PermissionSortingFields;
  setOrderBy: (val: PermissionSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: PermissionSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
}

export const PermissionsContext = createContext<IPermissionsContext>({} as IPermissionsContext);

export const PermissionsProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [permissionsUpdate, setPermissionsUpdate] = useState(false);
  const toggleUpdate = (): void => setPermissionsUpdate(!permissionsUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<PermissionSortingFields>(toggleUpdate, 'displayName', 'desc');

  const [permissions, setPermissions] = useState<PermissionModel[]>([]);
  const [loading, setLoading] = useState(false);

  return (
    <PermissionsContext.Provider
      value={{
        permissionsUpdate,
        togglePermissionsUpdate: toggleUpdate,
        permissions,
        setPermissions,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
